import { invoke } from "@tauri-apps/api/core";
import { isEqual } from "es-toolkit";
import { produce } from "immer";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { basename } from "pathe";

import { Logger } from "@moment/logging";

import { getPty } from "../_interop/pty";
import { env } from "../env/env";
import {
	type TerminalInstanceKey,
	type TerminalSpawnOptions,
	type TerminalState,
	initialTerminalState,
} from "./terminal.types";

const logger = new Logger("terminal");

/**
 * Gets an environment variable at runtime from the native OS.
 * Only works in desktop app context.
 */
const getEnvVar = async (name: string): Promise<string | null> => {
	if (!env.isDesktopApp) {
		return null;
	}
	try {
		return await invoke<string | null>("get_env", { name });
	} catch (error) {
		logger.warn("Failed to get env var", { name, error });
		return null;
	}
};

const doesPowerShellCoreExist = async () => {
	if (!env.isDesktopApp) {
		return false;
	}

	try {
		const result: any = await invoke("does_powershell_core_exist");
		const decoder = new TextDecoder();
		const stderr = decoder.decode(new Uint8Array(result["stderr"]));
		if (stderr) {
			throw new Error(stderr);
		}

		const statusCode: number = result["status"];
		return statusCode === 0;
	} catch (error) {
		logger.error("Failed to check if PowerShell Core exists", { error });
	}

	return false;
};

export type ShellInfo = {
	path: string;
	name: string;
};

/**
 * Returns the default shell for the current platform.
 * Fetches $SHELL at runtime from the native OS.
 *
 *   - Unix/macOS: Uses $SHELL environment variable, falls back to /bin/zsh
 *   - Windows: Uses %COMSPEC% environment variable, falls back to powershell.exe
 */
export const getDefaultShell = async (): Promise<ShellInfo> => {
	let shellPath: string;

	if (env.isWindows) {
		const psVersion = await doesPowerShellCoreExist();
		if (psVersion) {
			shellPath = "pwsh.exe";
		} else {
			shellPath = "powershell.exe";
		}
	} else {
		const shell = await getEnvVar("SHELL");
		if (shell) {
			logger.info("Using runtime SHELL", { shell });
			shellPath = shell;
		} else {
			logger.warn("SHELL env var not found, falling back to /bin/zsh");
			shellPath = "/bin/zsh";
		}
	}

	const name = basename(shellPath);
	return { path: shellPath, name: name || "Terminal" };
};

export const $defaultShell = atom(async () => {
	return getDefaultShell();
});
$defaultShell.debugLabel = "defaultShell";

export type TerminalHandle = {
	write: (data: string) => void;
	resize: (cols: number, rows: number) => void;
	onData: (callback: (data: Uint8Array) => void) => { dispose: () => void };
	onExit: (callback: (exitCode: number | null) => void) => { dispose: () => void };
	kill: () => void;
};

export const TERMINAL_INSTANCE_KEY_SEPARATOR = ":";

export const keyToString = (key: TerminalInstanceKey): string =>
	`${key.documentId}${TERMINAL_INSTANCE_KEY_SEPARATOR}${key.terminalId}`;

export const stringToKey = (s: string) => {
	const idx = s.indexOf(TERMINAL_INSTANCE_KEY_SEPARATOR);
	if (idx === -1) {
		throw new Error("Invalid terminal instance key string");
	}

	const documentId = s.slice(0, idx);
	const terminalId = s.slice(idx + 1);
	return { documentId, terminalId };
};

const $terminalStateInternal = atomFamily(
	(_key: TerminalInstanceKey) => atom(initialTerminalState),
	isEqual
);

// Store handles persistently so we can reconnect after unmount
const terminalHandles = new Map<string, TerminalHandle>();

export const $terminalState = atomFamily((key: TerminalInstanceKey) => {
	const stateAtom = atom(
		(get) => get($terminalStateInternal(key)),
		(get, set, update: Partial<TerminalState>) => {
			const current = get($terminalStateInternal(key));
			const newState = produce(current, (draft) => {
				Object.assign(draft, update);
			});
			set($terminalStateInternal(key), newState);
		}
	);
	stateAtom.debugLabel = `terminalState:${keyToString(key)}`;
	return stateAtom;
}, isEqual);

export const $spawnTerminal = atom(
	null,
	async (
		get,
		set,
		params: {
			key: TerminalInstanceKey;
			options?: TerminalSpawnOptions;
		}
	): Promise<TerminalHandle | null> => {
		const { key, options = {} } = params;

		if (!env.isDesktopApp) {
			logger.warn("Terminal spawn not available in non-desktop environment");
			return null;
		}

		const currentState = get($terminalStateInternal(key));
		const keyStr = keyToString(key);

		logger.info("spawnTerminal called", {
			key: keyStr,
			currentStatus: currentState.status,
			hasExistingHandle: terminalHandles.has(keyStr),
		});

		// If already running, return the existing handle for reconnection
		if (currentState.status === "running") {
			const existingHandle = terminalHandles.get(keyStr);
			if (existingHandle) {
				logger.info("Reconnecting to existing terminal", { key: keyStr });
				return existingHandle;
			}
			logger.warn("Terminal state is running but no handle found", { key: keyStr });
		}

		if (currentState.status === "spawning") {
			logger.warn("Terminal already spawning", { key: keyStr });
			return null;
		}

		set($terminalStateInternal(key), {
			status: "spawning",
			pid: null,
			exitCode: null,
		});

		try {
			const pty = getPty();

			const command = options.command ?? (await getDefaultShell()).path;
			const args = options.args ?? [];

			logger.info("Spawning terminal", { key: keyToString(key), command, args });

			const process = await pty.spawn(command, args, {
				cols: options.cols ?? 80,
				rows: options.rows ?? 24,
				cwd: options.cwd,
				env: options.env,
			});

			set($terminalStateInternal(key), {
				status: "running",
				pid: process.pid,
				exitCode: null,
			});

			let dataCallback: ((data: Uint8Array) => void) | null = null;
			let exitCallback: ((exitCode: number | null) => void) | null = null;
			let bufferedData: Uint8Array[] = [];

			process.onData((data) => {
				if (dataCallback) {
					dataCallback(data);
				} else {
					bufferedData.push(data);
				}
			});

			process.onExit(({ exitCode }) => {
				logger.info("Terminal process exited", {
					key: keyStr,
					exitCode,
				});
				set($terminalStateInternal(key), {
					status: "exited",
					pid: null,
					exitCode,
				});
				terminalHandles.delete(keyStr);
				dataCallback = null;
				if (exitCallback) {
					exitCallback(exitCode);
					exitCallback = null;
				}
			});

			const handle: TerminalHandle = {
				write: (data: string) => process.write(data),
				resize: (cols: number, rows: number) => process.resize(cols, rows),
				onData: (callback: (data: Uint8Array) => void) => {
					dataCallback = callback;
					// Flush buffered data
					for (const data of bufferedData) {
						callback(data);
					}
					bufferedData = [];
					return {
						dispose: () => {
							if (dataCallback === callback) {
								dataCallback = null;
							}
						},
					};
				},
				onExit: (callback: (exitCode: number | null) => void) => {
					exitCallback = callback;
					return {
						dispose: () => {
							if (exitCallback === callback) {
								exitCallback = null;
							}
						},
					};
				},
				kill: () => {
					logger.info("Killing terminal", { key: keyStr });
					process.kill();
					terminalHandles.delete(keyStr);
					set($terminalStateInternal(key), {
						status: "exited",
						pid: null,
						exitCode: null,
					});
				},
			};

			terminalHandles.set(keyStr, handle);
			return handle;
		} catch (error) {
			logger.error("Failed to spawn terminal", { key: keyToString(key), cause: error });
			set($terminalStateInternal(key), {
				status: "idle",
				pid: null,
				exitCode: null,
			});
			return null;
		}
	}
);
$spawnTerminal.debugLabel = "spawnTerminal";
